import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { signIn, refreshAdminToken, validateSession } from "../../services/auth";

const ADMIN_TOKEN_KEY = "ticketwhats-admin:token";
const ADMIN_USER_KEY = "ticketwhats-admin:user";
const ADMIN_LAST_ACTIVITY_KEY = "ticketwhats-admin:lastActivity";
const ADMIN_IDLE_TIMEOUT = 10 * 60 * 1000; 

const AuthContext = createContext({
  user: null,
  token: null,
  loading: true,
  signed: false,
  signIn: () => Promise.resolve(),
  signOut: () => {}
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const lastActivityRef = useRef(Date.now());
  const timeoutRef = useRef(null);

  const readStoredUser = () => {
    const raw = localStorage.getItem(ADMIN_USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      localStorage.removeItem(ADMIN_USER_KEY);
      return null;
    }
  };

  const updateLastActivity = (timestamp) => {
    lastActivityRef.current = timestamp || Date.now();
    if (typeof window !== "undefined") {
      localStorage.setItem(ADMIN_LAST_ACTIVITY_KEY, lastActivityRef.current.toString());
    }
  };

  const trackActivity = () => {
    updateLastActivity(Date.now());
  };

  useEffect(() => {
    if (!user || !token) {
      return;
    }

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "click"];

    events.forEach(event => {
      window.addEventListener(event, trackActivity);
    });

    setInterval(() => {
      const lastActivity = parseInt(localStorage.getItem(ADMIN_LAST_ACTIVITY_KEY) || "0", 10);
      const now = Date.now();
      const elapsed = now - lastActivity;
      
      if (elapsed >= ADMIN_IDLE_TIMEOUT) {
        clearAdminSession();
      }
    }, 30 * 1000);

    updateLastActivity();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, trackActivity);
      });
    };
  }, [user, token]);

  const clearAdminSession = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    localStorage.removeItem(ADMIN_LAST_ACTIVITY_KEY);
    setToken(null);
    setUser(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadStoredSession = async () => {
      const storedToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      const storedUser = readStoredUser();
      const storedLastActivity = localStorage.getItem(ADMIN_LAST_ACTIVITY_KEY);

      if (!storedToken) {
        if (isMounted) {
          setToken(null);
          setUser(null);
          setLoading(false);
        }
        return;
      }

      setToken(storedToken);

      if (storedUser?.super === true) {
        setUser(storedUser);
      }

      if (storedLastActivity) {
        lastActivityRef.current = parseInt(storedLastActivity, 10);
      } else {
        updateLastActivity(Date.now());
      }

      try {
        const refreshResult = await refreshAdminToken();
        const currentUser = refreshResult?.user || refreshResult?.profile || refreshResult;

        if (!currentUser || currentUser.super !== true) {
          throw new Error("FORBIDDEN");
        }

        if (isMounted) {
          setUser(currentUser);
          localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(currentUser));
        }
        
        updateLastActivity();
        setLoading(false);
      } catch (err) {
        const status = err?.response?.status;
        const message = err?.message;
        
        if (status === 401 || message === "ERR_SESSION_EXPIRED" || message === "NO_TOKEN") {
          if (isMounted) {
            clearAdminSession();
          }
        } else if (status === 403 || message === "FORBIDDEN") {
          if (isMounted) {
            setUser(null);
          }
        } else {
          if (isMounted && !storedUser?.super) {
            setUser(null);
          }
        }
        
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadStoredSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const signInHandler = async (email, password) => {
    try {
      const { token: newToken, user: newUser } = await signIn(email, password);

      if (newUser.super !== true) {
        clearAdminSession();
        const error = new Error("FORBIDDEN");
        error.code = "FORBIDDEN";
        throw error;
      }

      localStorage.setItem(ADMIN_TOKEN_KEY, newToken);
      localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(newUser));
      localStorage.setItem(ADMIN_LAST_ACTIVITY_KEY, Date.now().toString());
      setToken(newToken);
      setUser(newUser);
    } catch (err) {
      console.error("[AdminAuth] signIn failed", {
        message: err.message,
        code: err.code,
        status: err.response?.status,
        url: err.config?.url
      });
      throw err;
    }
  };

  const signOutHandler = () => {
    clearAdminSession();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        signed: !!user && !!token && user.super === true,
        signIn: signInHandler,
        signOut: signOutHandler
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);